import { PanelProps } from '@blueprintjs/core';
import * as React from 'react';

import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { DataSourceType } from 'src/constants/dataSourceTypeDescription';
import { DataSourceCards } from 'src/containers/DataSourceCreation/DataSourceCards';
import { DataSourcePanelContainer } from 'src/containers/ProjectResourceDialog/DataSourcePanelContainer';
import { ProjectResourceDialogFooterProps } from 'src/containers/ProjectResourceDialog/types';
import { useLocalVirtualGraph } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useLocalVirtualGraphValue';
import { ProjectVirtualGraphPreviewDialogDataSourceCreationPanel } from 'src/containers/ProjectVirtualGraphPreviewDialog/ProjectVirtualGraphPreviewDialogDataSourceCreationPanel';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { DataSource } from 'src/store/state/dataSources';
import * as copy from 'src/templates/copy';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type ProjectVirtualGraphPreviewDialogDataSourceSelectionPanelProps = {
  closePrevPanel: () => void;
  setFooterProps: (footerProps: ProjectResourceDialogFooterProps) => void;
  type: DataSourceType;
};

export const ProjectVirtualGraphPreviewDialogDataSourceSelectionPanel: React.VFC<
  PanelProps<ProjectVirtualGraphPreviewDialogDataSourceSelectionPanelProps>
> = ({ closePanel, closePrevPanel, openPanel, setFooterProps, type }) => {
  const dispatch = useReduxDispatch();
  const isMounted = useIsMounted();
  const connection = useReduxSelector(connectionSelector);
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);

  const [localVirtualGraph, setLocalVirtualGraph] = useLocalVirtualGraph();

  const dataSources = React.useMemo(
    () => dataSourcesByType[type] || [],
    [dataSourcesByType, type]
  );
  const isPendingOptions = React.useMemo(() => {
    return dataSources.some((dataSource) => !dataSource.options);
  }, [dataSources]);

  const closePrevPanels = React.useCallback(() => {
    closePanel();
    closePrevPanel();
  }, [closePanel, closePrevPanel]);

  const handleSelectCreate = React.useCallback(() => {
    openPanel({
      renderPanel: ProjectVirtualGraphPreviewDialogDataSourceCreationPanel,
      props: {
        closePrevPanels,
        setFooterProps,
        type,
      },
    });
  }, [closePrevPanels, openPanel, setFooterProps, type]);

  const handleSelect = React.useCallback(
    async (dataSource: DataSource) => {
      if (!connection || !localVirtualGraph) {
        return;
      }

      // refreshing data sources will have cleared table information,
      // so we need to refetch here. Since we're clearing the selected table
      // below no matter what, we won't run into problems refreshing tables
      await dispatch(refreshDataSourceTables(dataSource.id));

      // even if the user selects the current data source, we will clear
      // the table here
      setLocalVirtualGraph({
        ...localVirtualGraph,
        name: '',
        connectionId: connection?.id || null,
        dataSourceEndpoint: dataSource.options?.['jdbc.url'],
        dataSourceId: dataSource?.id,
        dataSourceType: dataSource?.type,
        from: {
          name: '',
          text: '',
          value: '',
        },
        fields: [],
        previewRows: [],
        errors: undefined,
        isSettingsOpenKey: Date.now(),
      });

      closePrevPanels();
    },
    [
      closePrevPanels,
      connection,
      dispatch,
      localVirtualGraph,
      setLocalVirtualGraph,
    ]
  );

  React.useEffect(() => {
    if (isMounted.current) {
      setFooterProps({
        handleBackButtonClick: closePanel,
      });
    }
  }, [closePanel, isMounted, setFooterProps]);

  return (
    <DataSourcePanelContainer
      connectionType={type}
      warningMessage={copy.components.virtualGraphPreviewDialog.warningTitle}
    >
      <DataSourceCards
        isLoading={isPendingOptions}
        onCreateDataSource={handleSelectCreate}
        onSelectDataSource={handleSelect}
        type={type}
      />
    </DataSourcePanelContainer>
  );
};
